import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search, X } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import Pagination from "../components/Pagination";
import MainLayout from "../Layouts/main";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import { usePaginationSearch } from "@utils";
import WorkflowFolders from "./WorkflowFolders";
import FilesTable from "./FilesTable";
import DeleteFileDialog from "./DeleteFileDialog";

export function Files() {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const itemsPerPage = 30;
    const [pagination, setPagination] = useState(null);

    const { currentPage, searchTerm, debouncedSearchTerm, updatePage, updateSearch, clearSearch } =
        usePaginationSearch();

    const { call } = useBluelagoon({ method: "getFilesList" });
    const { call: deleteFileCall } = useBluelagoon({ method: "deleteFile" });
    const { call: getFileSignedUrlCall } = useBluelagoon({ method: "getFileSignedUrl" });

    const loadRequestIdRef = useRef(0);

    const loadFiles = useCallback(
        async (page = 1) => {
            const requestId = ++loadRequestIdRef.current;
            try {
                initialLoading ? setInitialLoading(true) : setLoading(true);
                const response = await call({
                    limit: itemsPerPage,
                    offset: (page - 1) * itemsPerPage,
                    search: debouncedSearchTerm || "",
                    ...(selectedWorkflowId ? { workflowId: selectedWorkflowId } : {}),
                });
                if (requestId !== loadRequestIdRef.current) return;
                if (response?.files) {
                    setFiles(response.files);
                    setPagination(response.pagination);
                }
            } catch (err) {
                if (requestId !== loadRequestIdRef.current) return;
                console.error("Error loading files:", err);
                setError(t("files.errors.loadFailed"));
            } finally {
                if (requestId === loadRequestIdRef.current) {
                    setInitialLoading(false);
                    setLoading(false);
                }
            }
        },
        [call, itemsPerPage, debouncedSearchTerm, selectedWorkflowId, initialLoading, t]
    );

    const handleDelete = async () => {
        if (!fileToDelete) return;
        try {
            await deleteFileCall({ fileId: fileToDelete.id });
            setDeleteDialogOpen(false);
            setFileToDelete(null);
            loadFiles(currentPage);
        } catch (err) {
            console.error("Error deleting file:", err);
            setError(t("files.errors.deleteFailed"));
        }
    };

    const handleDownload = async (file) => {
        try {
            const response = await getFileSignedUrlCall({ fileId: file.id });
            if (response?.signedUrl) {
                window.open(
                    response.signedUrl,
                    "_blank",
                    "noopener,noreferrer"
                );
            } else {
                setError(t("files.errors.downloadUrlFailed"));
            }
        } catch (err) {
            console.error("Error getting signed URL:", err);
            setError(t("files.errors.downloadFailed"));
        }
    };

    const handleWorkflowSelect = (workflowId) => {
        setSelectedWorkflowId(workflowId);
        updatePage(1);
    };

    useEffect(() => {
        loadFiles(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, debouncedSearchTerm, selectedWorkflowId]);

    if (initialLoading) {
        return (
            <MainLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-[150px]" />
                    <div className="flex gap-2">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-9 w-[120px] rounded-lg" />
                        ))}
                    </div>
                    <Skeleton className="h-9 w-[300px]" />
                    <Skeleton className="h-[300px] w-full rounded-md" />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-4">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
                    {t("files.pageTitle")}
                </h2>

                <WorkflowFolders
                    selectedWorkflowId={selectedWorkflowId}
                    onSelect={handleWorkflowSelect}
                />

                <div className="flex items-center gap-4 pb-2">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            type="text"
                            placeholder={t("files.search.placeholder")}
                            value={searchTerm}
                            onChange={(e) => updateSearch(e.target.value)}
                            className="pl-10 pr-10"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => clearSearch()}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="text-red-500 p-4 bg-red-50 rounded">{error}</div>
                )}

                {loading ? (
                    <Skeleton className="h-[300px] w-full rounded-md" />
                ) : (
                    <div className="space-y-4">
                        <FilesTable
                            files={files}
                            searchTerm={debouncedSearchTerm}
                            onDownload={handleDownload}
                            onDelete={(file) => {
                                setFileToDelete(file);
                                setDeleteDialogOpen(true);
                            }}
                        />
                        {pagination && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(pagination.total / itemsPerPage)}
                                onPageChange={updatePage}
                                itemsPerPage={itemsPerPage}
                                totalItems={pagination.total}
                                className="pt-4"
                            />
                        )}
                    </div>
                )}
            </div>

            <DeleteFileDialog
                open={deleteDialogOpen}
                file={fileToDelete}
                onConfirm={handleDelete}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setFileToDelete(null);
                }}
            />
        </MainLayout>
    );
}
